/**
 * SMS segments and cost (F-070), shared by the composer and the server so the
 * number on screen is the number that is sent.
 *
 * GSM-7 text fits 160 characters in one segment and 153 per segment once it
 * is split; one character outside GSM-7 (an emoji, Arabic, a curly quote)
 * makes the whole message UCS-2: 70, then 67. That switch is the usual cost
 * surprise, so the composer names the character that caused it.
 */

const GSM7 =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM7_EXTENDED = '^{}\\[~]|€\f';

export interface SmsSegments {
  encoding: 'GSM-7' | 'UCS-2';
  characters: number;
  segments: number;
  /** The first character that forced UCS-2, when one did. */
  unicodeBecause: string | null;
  /** Characters left before the next segment starts. */
  remaining: number;
}

export function smsSegments(text: string): SmsSegments {
  const chars = [...text];
  const offender = chars.find((ch) => !GSM7.includes(ch) && !GSM7_EXTENDED.includes(ch)) ?? null;
  if (offender === null) {
    const units = chars.reduce((total, ch) => total + (GSM7_EXTENDED.includes(ch) ? 2 : 1), 0);
    const segments = units === 0 ? 0 : units <= 160 ? 1 : Math.ceil(units / 153);
    const capacity = segments <= 1 ? 160 : segments * 153;
    return { encoding: 'GSM-7', characters: units, segments, unicodeBecause: null, remaining: capacity - units };
  }
  const units = [...text].reduce((total, ch) => total + (ch.codePointAt(0)! > 0xffff ? 2 : 1), 0);
  const segments = units <= 70 ? 1 : Math.ceil(units / 67);
  const capacity = segments <= 1 ? 70 : segments * 67;
  return { encoding: 'UCS-2', characters: units, segments, unicodeBecause: offender, remaining: capacity - units };
}

/** Indicative price per segment, in US cents, by destination. An estimate, and labelled as one. */
export const SMS_PRICE_CENTS: Record<string, number> = { AE: 3.2, SA: 4.0, IN: 0.3, GB: 4.5, US: 0.8, DEFAULT: 5.0 };

/** The country an E.164 number belongs to, for the prefixes this product prices and routes. */
export function countryOfNumber(e164: string): string {
  const table: Array<[string, string]> = [['+971', 'AE'], ['+966', 'SA'], ['+91', 'IN'], ['+44', 'GB'], ['+1', 'US']];
  return table.find(([prefix]) => e164.startsWith(prefix))?.[1] ?? 'DEFAULT';
}
